import React, { FC, useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ISelectOption } from 'src/components/ui/select/SelectOption'

interface ISelectProps {
  className?: string
  options: ISelectOption[]
  selectedItem: ISelectOption | null
  onSelect: (option: ISelectOption) => any
  placeholder: string
  variant?: 'default' | 'minimal'
}

const AnimatedText: FC<{ children: React.ReactNode; isActive?: boolean }> = ({
  children,
  isActive,
}) => (
  <span className="relative block overflow-hidden leading-normal">
    {/* First span: Normal flow, determines container size */}
    <span
      className={`block transition-transform duration-500 ease-[cubic-bezier(0.19,1,0.22,1)] transform ${
        !isActive ? 'group-hover:-translate-y-full' : ''
      } will-change-transform [backface-visibility:hidden] [transform:translateZ(0)]`}
    >
      {children}
    </span>
    {/* Second span: Absolute, slides in from bottom */}
    {!isActive && (
      <span
        className="absolute inset-0 block transition-transform duration-500 ease-[cubic-bezier(0.19,1,0.22,1)] transform translate-y-full group-hover:translate-y-0 will-change-transform [backface-visibility:hidden] [transform:translateZ(0)]"
        aria-hidden="true"
      >
        {children}
      </span>
    )}
  </span>
)

const Select: FC<ISelectProps> = ({
  className = '',
  options,
  selectedItem,
  onSelect,
  placeholder,
  variant = 'default',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  const handleOptionClick = (option: ISelectOption) => {
    onSelect(option)
    setIsOpen(false)
  }

  const handleDocumentClick = (event: MouseEvent) => {
    const isClickedOutside =
      selectRef.current && !selectRef.current.contains(event.target as Node)

    if (isClickedOutside) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick)

    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [])

  return (
    <div
      style={{ outline: 'none' }}
      className={`${className} border bg-highlight-subtle-lowest outline-none focus-within:outline-none rounded-large ${
        isOpen || !!selectedItem
          ? 'border-red'
          : 'border-secondaryGray500 dark:border-white/20 hover:border-red focus-within:border-red'
      } relative z-40 transition-colors duration-300 ease-out`}
      ref={selectRef}
    >
      <button
        type="button"
        style={{ outline: 'none' }}
        className="px-5 md:px-6 h-14 w-full text-left flex justify-between items-center text-black dark:text-white outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 cursor-pointer select-none"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className={
            !selectedItem
              ? 'text-secondaryGray600 dark:text-slate-400 lowercase truncate'
              : 'font-semibold truncate text-black dark:text-white'
          }
        >
          {(selectedItem && selectedItem.label) || placeholder}
        </span>
        <span
          className={`transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] shrink-0 ml-3 text-secondaryGray600 dark:text-slate-400 ${
            isOpen ? 'rotate-180 text-red' : ''
          }`}
        >
          <svg
            width="12"
            height="8"
            viewBox="0 0 12 8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M1.5 2.5L6 6.5L10.5 2.5" />
          </svg>
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 8, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
            style={{
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
            }}
            className="absolute top-full left-0 right-0 mt-2 z-50 shadow-elevation-highest bg-highlight-base dark:bg-black/70 backdrop-filter backdrop-blur-higher border border-outline-lower dark:border-outline/20 rounded-container-medium overflow-hidden"
          >
            <div
              data-lenis-prevent
              className="py-2 px-2 flex flex-col gap-1 max-h-64 overflow-y-auto overscroll-contain no-scrollbar [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onWheel={(e) => e.stopPropagation()}
              role="listbox"
            >
              {options.map((option, index) => {
                const isSelected = selectedItem?.optionId === option.optionId
                return (
                  <motion.div
                    key={option.optionId}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: index * 0.03 + 0.04 }}
                  >
                    <button
                      type="button"
                      onClick={() => handleOptionClick(option)}
                      role="option"
                      aria-selected={isSelected}
                      className={`text-on-surface font-palanquin font-bold transition-all duration-300 relative flex items-center px-4 py-2.5 rounded-full [webkit-font-smoothing:antialiased] w-full justify-start text-sm md:text-base group cursor-pointer outline-none focus:outline-none select-none ${
                        isSelected
                          ? '!opacity-100 !bg-surface dark:!bg-white/10 shadow-sm text-primary dark:text-primary'
                          : 'opacity-80 hover:opacity-100 hover:bg-surface/80 dark:hover:bg-white/10'
                      }`}
                    >
                      <AnimatedText isActive={isSelected}>{option.label}</AnimatedText>
                    </button>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Select

